import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import SiteGate from "@/components/SiteGate";
import AppShell from "@/components/AppShell";
import Overview from "@/pages/Overview";
import TradingJournal from "@/pages/TradingJournal";
import Strategies from "@/pages/Strategies";
import StrategyDetail from "@/pages/StrategyDetail";
import Analytics from "@/pages/Analytics";
import KiCoach from "@/pages/KiCoach";
import TaxTool from "@/pages/TaxTool";
import Settings from "@/pages/Settings";
import AdminSupport from "@/pages/AdminSupport";
import AdminMembers from "@/pages/AdminMembers";
import AdminBroadcast from "@/pages/AdminBroadcast";
import AdminCoupons from "@/pages/AdminCoupons";
import Landing from "@/pages/Landing";
import { LoginPage, RegisterPage, ForgotPage, ResetPage, VerifyPage } from "@/pages/Auth";
import { Impressum, Datenschutz, AGB, ImpressumApp, DatenschutzApp, AGBApp } from "@/pages/Legal";
import { QuizOverview, QuizModule } from "@/pages/Quiz";
import AmiLayout, { AMI_SECTIONS } from "@/pages/ami/AmiLayout";
import AmiOverview from "@/pages/ami/AmiOverview";
import AmiMarket from "@/pages/ami/AmiMarket";
import AmiHistory from "@/pages/ami/AmiHistory";
import AmiConnections from "@/pages/ami/AmiConnections";
import AmiMonitoring from "@/pages/ami/AmiMonitoring";
import AmiSettings from "@/pages/ami/AmiSettings";
import AmiPlaceholder from "@/pages/ami/AmiPlaceholder";
import AmiIntel from "@/pages/ami/AmiIntel";
import AmiPredictions from "@/pages/ami/AmiPredictions";
import AmiStrategies from "@/pages/ami/AmiStrategies";
import AmiBacktesting from "@/pages/ami/AmiBacktesting";
import AmiStopLoss from "@/pages/ami/AmiStopLoss";
import AmiRisk from "@/pages/ami/AmiRisk";

function AmiOnly({ children }) {
  const { realUser } = useAuth();
  if (realUser === null) return <div className="min-h-screen grid place-items-center text-muted-foreground text-sm">Sitzung wird geprüft…</div>;
  if (!realUser?.ami_owner) return <Navigate to="/app" replace />;
  return children;
}

function Protected({ children }) {
  const { user, state } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground text-sm">Sitzung wird geprüft…</div>;
  if (!user) return <Navigate to="/anmelden" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, isAdmin } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground text-sm">Sitzung wird geprüft…</div>;
  if (!isAdmin) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <div className="app-root">
      <SiteGate>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/anmelden" element={<LoginPage />} />
            <Route path="/registrieren" element={<RegisterPage />} />
            <Route path="/passwort-vergessen" element={<ForgotPage />} />
            <Route path="/passwort-zuruecksetzen" element={<ResetPage />} />
            <Route path="/anmeldung/bestaetigen/:token" element={<VerifyPage />} />
            <Route path="/anmeldung/bestaetigen" element={<VerifyPage />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/agb" element={<AGB />} />

            {/* Protected app */}
            <Route path="/app" element={<Protected><AppShell /></Protected>}>
              <Route index element={<Overview />} />
              <Route path="journal" element={<TradingJournal />} />
              <Route path="strategien" element={<Strategies />} />
              <Route path="strategien/:id" element={<StrategyDetail />} />
              <Route path="analyse" element={<Analytics />} />
              <Route path="ki-coach" element={<KiCoach />} />
              <Route path="steuer" element={<TaxTool />} />
              <Route path="einstellungen" element={<Settings />} />
              <Route path="quiz" element={<QuizOverview />} />
              <Route path="quiz/:module" element={<QuizModule />} />
              <Route path="support" element={<AdminOnly><AdminSupport /></AdminOnly>} />
              <Route path="mitglieder" element={<AdminOnly><AdminMembers /></AdminOnly>} />
              <Route path="broadcast" element={<AdminOnly><AdminBroadcast /></AdminOnly>} />
              <Route path="rabattcodes" element={<AdminOnly><AdminCoupons /></AdminOnly>} />
              <Route path="ami" element={<AmiOnly><AmiLayout /></AmiOnly>}>
                <Route index element={<AmiOverview />} />
                <Route path="live" element={<AmiMarket />} />
                <Route path="historie" element={<AmiHistory />} />
                <Route path="intelligence" element={<AmiIntel />} />
                <Route path="prognosen" element={<AmiPredictions />} />
                <Route path="strategien" element={<AmiStrategies />} />
                <Route path="backtesting" element={<AmiBacktesting />} />
                <Route path="stop-loss" element={<AmiStopLoss />} />
                <Route path="risk" element={<AmiRisk />} />
                <Route path="verbindungen" element={<AmiConnections />} />
                <Route path="monitoring" element={<AmiMonitoring />} />
                <Route path="einstellungen" element={<AmiSettings />} />
                {AMI_SECTIONS.filter((s) => s.phase).map((s) => (
                  <Route key={s.to} path={s.to} element={<AmiPlaceholder title={s.label} phase={s.phase} />} />
                ))}
              </Route>
              <Route path="impressum" element={<ImpressumApp />} />
              <Route path="datenschutz" element={<DatenschutzApp />} />
              <Route path="agb" element={<AGBApp />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </SiteGate>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
